/**
 * ============================================================================
 * WORKLOAD TRACKING SERVICE - PHASE 4 COMPLIANT
 * ============================================================================
 *
 * Facade coordinating focused workload-tracking sub-services.
 *
 * @phase 4
 * @module services/features/workload-tracking
 */

import { IService, ServiceResponse } from "@/services/core/service-registry";
import { logger } from "@/lib/logger";
import type {
  CreateWorkloadTrackingRequest,
  UpdateWorkloadTrackingRequest,
  WorkloadSummary,
  WorkloadTracking,
  WorkloadTrend,
} from "@/types/time-management";
import { WorkloadTrackingValidationService } from "./workload-tracking-validation.service";
import { WorkloadTrackingCrudService } from "./workload-tracking-crud.service";
import { WorkloadTrackingAnalyticsService } from "./workload-tracking-analytics.service";
import { WorkloadTrackingPredictionService } from "./workload-tracking-prediction.service";

export { WorkloadTrackingValidationService } from "./workload-tracking-validation.service";
export { WorkloadTrackingCrudService } from "./workload-tracking-crud.service";
export { WorkloadTrackingAnalyticsService } from "./workload-tracking-analytics.service";
export { WorkloadTrackingPredictionService } from "./workload-tracking-prediction.service";

/**
 * Minimal facade interface for workload tracking.
 * Sub-services can be imported directly for advanced operations.
 */
export interface IWorkloadTrackingService extends IService {
  getUserWorkload(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadTracking[]>>;
  getWorkloadSummary(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadSummary>>;
  getProductivityTrends(
    userId: string,
    dateRange: { start: string; end: string },
    metric: "hours" | "tasks" | "productivity"
  ): Promise<ServiceResponse<WorkloadTrend[]>>;
  upsertWorkloadEntry(
    userId: string,
    date: string,
    data: CreateWorkloadTrackingRequest
  ): Promise<ServiceResponse<WorkloadTracking>>;
}

/**
 * Facade for workload tracking services.
 * Delegates to focused sub-services for validation, CRUD, analytics, and predictions.
 */
export class WorkloadTrackingService implements IWorkloadTrackingService {
  public readonly name = "WorkloadTrackingService";
  private initialized = false;
  private validationService: WorkloadTrackingValidationService;
  private crudService: WorkloadTrackingCrudService;
  private analyticsService: WorkloadTrackingAnalyticsService;
  private predictionService: WorkloadTrackingPredictionService;

  constructor() {
    this.validationService = new WorkloadTrackingValidationService();
    this.crudService = new WorkloadTrackingCrudService(this.validationService);
    this.analyticsService = new WorkloadTrackingAnalyticsService(this.crudService);
    this.predictionService = new WorkloadTrackingPredictionService(
      this.crudService,
      this.analyticsService
    );
  }

  async initialize(): Promise<void> {
    if (this.initialized) {return;}

    try {
      logger.info("📊 Initializing Workload Tracking Service (Phase 4)...");
      await this.validationService.initialize();
      await this.crudService.initialize();
      await this.analyticsService.initialize();
      await this.predictionService.initialize();
      this.initialized = true;
      logger.info("✅ Workload Tracking Service initialized");
    } catch (error) {
      logger.error("❌ Workload Tracking Service initialization failed:", { error: error });
      throw error;
    }
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    await this.validationService.dispose();
    await this.crudService.dispose();
    await this.analyticsService.dispose();
    await this.predictionService.dispose();
    logger.info("🔌 Workload Tracking Service disposed");
  }

  async getUserWorkload(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadTracking[]>> {
    return this.crudService.getUserWorkload(userId, dateRange);
  }

  async getWorkloadSummary(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadSummary>> {
    return this.analyticsService.getWorkloadSummary(userId, dateRange);
  }

  async getProductivityTrends(
    userId: string,
    dateRange: { start: string; end: string },
    metric: "hours" | "tasks" | "productivity"
  ): Promise<ServiceResponse<WorkloadTrend[]>> {
    return this.analyticsService.getProductivityTrends(userId, dateRange, metric);
  }

  async upsertWorkloadEntry(
    userId: string,
    date: string,
    data: CreateWorkloadTrackingRequest
  ): Promise<ServiceResponse<WorkloadTracking>> {
    return this.crudService.upsertWorkloadEntry(userId, date, data);
  }
}

export const workloadTrackingService = new WorkloadTrackingService();
export { workloadTrackingService as default };
export type { CreateWorkloadTrackingRequest, UpdateWorkloadTrackingRequest };
